import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import ImageWithDisplayName from '@/common/components/ImageWithDisplayName'
import ListFilters from '@/common/components/ListFilters'
import ListHeader from '@/common/components/ListHeader'
import Skeleton from '@/common/components/Skeleton'
import Table from '@/common/components/Table'
import TextField from '@/common/components/TextField'
import { solicitationKindOptions } from '@/common/constants/maintenance/solicitationKind.constant'
import { solicitationStatusOptions } from '@/common/constants/maintenance/solicitationStatus.constant'
import useCompany from '@/common/hooks/queries/useCompany'
import { useMaintenanceSolicitations } from '@/common/hooks/queries/useMaintenanceSolicitations'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { CompanyBranchData } from '@/common/types/companyBranches.type'
import {
  MaintenanceSolicitationData,
  MaintenanceSolicitationTableItem,
} from '@/common/types/maintenance/maintenanceSolicitation'
import { cn } from '@/common/utils/cn'
import { defaultColumns } from '@/features/privateCompanies/constants/maintenanceSolicitations/columns.constant'
import { useAvailableCompanyBranches } from '@/features/privateCompanies/hooks/companyBranches/useAvailableCompanyBranches'
import { MaintenanceSolicitationsTransformer } from '@/features/privateCompanies/services/maintenanceSolicitations/maintenanceSolicitationsTransformer'
import { ChevronsRightIcon, SearchIcon } from 'lucide-react'
import { useParams } from 'react-router-dom'

const List = () => {
  const { id } = useParams()
  const { filterParams, setFilterParams } = useFilterParams()

  const [search, setSearch] = useDebounce(filterParams.search || '')
  const [searchClientBranch, setSearchClientBranch] = useDebounce('')

  const { data: companyData, isPending: companyDataIsPending } = useCompany({ id: id || '' })

  const { data: clientBranchesOptions } = useAvailableCompanyBranches({
    companyId: id,
    filters: { search: searchClientBranch },
    mapper: Mapper.mapToOptions<CompanyBranchData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
  })

  const {
    data: maintenanceSolicitations,
    meta,
    isPending: maintenanceSolicitationsIsPending,
    error: maintenanceSolicitationsError = {} as any,
    refetch: refetchMaintenanceSolicitations,
  } = useMaintenanceSolicitations({
    filters: {
      search,
      companyId: id || '',
      status: filterParams.status?.value,
      clientBranchId: filterParams.clientBranch?.value,
      kind: filterParams.kind?.value,
      page: filterParams.page || 1,
    },
    options: {
      enabled: !!id,
    },
    mapper: Mapper.mapWithTransformer<MaintenanceSolicitationData[], MaintenanceSolicitationTableItem[]>({
      transformer: MaintenanceSolicitationsTransformer.tableTransformer({ companyId: id }),
    }),
  })

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title={
          companyDataIsPending ? (
            <Skeleton className="w-96 h-10" />
          ) : (
            <div className="flex gap-2 items-center">
              <GoBackButton fallback={`/companies/${id}/maintenance`} searchLastRouteFromFallback />

              <ImageWithDisplayName
                imageUrl={companyData?.general_information?.company_logo?.url}
                displayName={companyData?.general_information?.fantasy_name}
              />

              <ChevronsRightIcon className="text-white-400" />

              <p>Solicitações de manutenção</p>
            </div>
          )
        }
        createButtonText="Nova Solicitação"
        dataTestid="create_maintenance_solicitation_button"
        operationKlassToCheckPermission="Admin::Maintenance::Solicitations::Create"
        disableCreateButton={maintenanceSolicitationsIsPending || maintenanceSolicitationsError?.status === 403}
      />

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={maintenanceSolicitationsError?.status}
        refetchFunction={refetchMaintenanceSolicitations}
      >
        <div className="h-[calc(100vh-128px)] flex flex-col gap-2">
          <div className="flex flex-col gap-4">
            <TextField
              name="search"
              LeftIcon={<SearchIcon />}
              placeholder="Pesquisar solicitações por ID ou Equipamento"
              className="rounded-sm shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              onChange={(e) => {
                setSearch((e.target as HTMLInputElement).value)
                setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }))
              }}
              value={filterParams.search}
            />
          </div>

          <ListFilters
            filters={[
              {
                className: 'min-w-32',
                onChange: (status) => setFilterParams((prev) => ({ ...prev, status })),
                value: filterParams.status,
                options: [{ label: 'Todos', value: '' }, ...solicitationStatusOptions],
                placeholder: 'Status',
              },
              {
                className: 'min-w-32',
                onChange: (clientBranch) => setFilterParams((prev) => ({ ...prev, clientBranch })),
                onInputChange: setSearchClientBranch,
                value: filterParams.clientBranch || '',
                options: [{ label: 'Todas', value: '' }, ...clientBranchesOptions],
                placeholder: 'Filial',
              },
              {
                className: 'min-w-32',
                onChange: (kind) => setFilterParams((prev) => ({ ...prev, kind })),
                value: filterParams.kind,
                options: [{ label: 'Todos', value: '' }, ...solicitationKindOptions],
                placeholder: 'Tipo de solicitação',
              },
            ]}
          />

          <Table
            className={cn('max-h-screen', {
              'shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]': !!maintenanceSolicitations?.length,
            })}
            columns={defaultColumns}
            isLoadingData={maintenanceSolicitationsIsPending}
            page={filterParams.page || 1}
            rows={maintenanceSolicitations || []}
            totalPages={meta.total_pages}
            totalCount={meta.total}
            hasPagination
            rowEmptyValue="--"
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  )
}

export default List
